<template>
  <section
    class="w-full max-w-full sm:max-w-md mx-auto bg-white dark:bg-gray-900"
  >
    <header class="px-4 sm:px-6 py-4 space-y-1.5">
      <div class="flex items-center justify-between gap-3">
        <h1 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ calendarHeaderTitle }}
        </h1>
        <a
          :href="calendarShareUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 dark:focus-visible:ring-gray-300"
          :aria-label="`Share ${calendarHeaderTitle} on WhatsApp`"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-3.5 w-3.5"
            aria-hidden="true"
          >
            <path
              d="M12.04 2C6.55 2 2.1 6.44 2.1 11.93c0 1.76.46 3.48 1.34 5L2 22l5.22-1.37a9.9 9.9 0 0 0 4.82 1.23h.01c5.48 0 9.94-4.44 9.94-9.93S17.53 2 12.04 2Zm5.79 14.1c-.24.67-1.36 1.25-1.89 1.33-.49.07-1.12.1-1.8-.11-.41-.13-.94-.31-1.62-.61-2.85-1.23-4.7-4.1-4.84-4.29-.14-.19-1.16-1.54-1.16-2.94s.73-2.09.99-2.38c.26-.28.57-.35.76-.35h.55c.17 0 .4-.06.62.47.24.58.83 2 .9 2.14.07.14.12.31.02.5-.1.19-.16.31-.31.48-.16.17-.33.38-.47.51-.16.16-.32.33-.14.64.18.31.8 1.32 1.72 2.14 1.18 1.05 2.17 1.38 2.48 1.53.31.16.49.14.67-.08.19-.23.79-.92 1-1.24.21-.31.43-.26.72-.16.3.1 1.86.88 2.18 1.03.31.16.52.24.59.38.07.14.07.81-.17 1.48Z"
            />
          </svg>
          <span>Share</span>
        </a>
      </div>
      <p class="text-sm text-gray-600 dark:text-gray-300">
        Har roza ka sehri end aur iftar time dekhein. Live aaj ke waqt ke liye
        <NuxtLink to="/" class="text-emerald-700 dark:text-emerald-300 hover:underline">
          roza time page
        </NuxtLink>
        par ja sakte hain.
      </p>
    </header>

    <!-- Calendar List -->
    <div class="divide-y divide-gray-100 dark:divide-gray-800">
      <template v-for="(day, index) in normalizedDays" :key="day.gregorianDate">
        <!-- Ashra Divider -->
        <div
          v-if="shouldShowAshraDivider(day, index)"
          class="px-4 sm:px-6 py-3 text-center bg-gray-50 dark:bg-gray-800/50"
        >
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ getAshraLabel(day.ashra) }}
          </p>
        </div>

        <!-- Eid Row -->
        <div
          v-if="day.isEid"
          class="px-4 sm:px-6 py-6 sm:py-8 text-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20"
        >
          <div class="text-2xl mb-2">🌙</div>
          <h3
            class="text-base font-medium text-green-700 dark:text-green-400 mb-1"
          >
            Eid-ul-Fitr
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ getEidDateDisplay(day) }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Ramadan 29 ya 30 rozay ka ho sakta hai. Final tareekh chand dekh kar
            confirm hoti hai.
          </p>
        </div>

        <!-- Regular Ramadan Day -->
        <button
          v-else
          class="w-full px-4 sm:px-6 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
          :class="
            isToday(day.gregorianDate) ? 'bg-gray-50 dark:bg-gray-800/30' : ''
          "
          @click="handleDayClick(day)"
        >
          <div class="flex items-center justify-between gap-4">
            <!-- Left: Roza + Date -->
            <div class="flex-1 min-w-0">
              <p
                class="text-sm tabular-nums mb-0.5"
                :class="
                  isToday(day.gregorianDate)
                    ? 'text-gray-900 dark:text-gray-100 font-semibold'
                    : 'text-gray-900 dark:text-gray-100 font-medium'
                "
              >
                Roza {{ day.rozaNumber }}
              </p>

              <p class="text-xs text-gray-400 dark:text-gray-500">
                {{ formatGregorianDate(day.gregorianDate) }}
              </p>
            </div>

            <!-- Right: Times (Iftar emphasized) -->
            <div class="text-right tabular-nums">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                {{ formatTime(day.iftarTime) }}
              </p>
              <p class="text-xs text-gray-400 dark:text-gray-500">
                {{ formatTime(day.sehriEnds) }}
              </p>
            </div>
          </div>
        </button>
      </template>
    </div>

    <!-- Day Detail Modal -->
    <Teleport to="body">
      <div
        v-if="showDayDetail"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4"
        @click.self="showDayDetail = false"
      >
        <div
          v-if="selectedDay"
          class="w-full sm:max-w-sm bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl p-6 shadow-xl"
        >
          <!-- Modal Header -->
          <div class="flex items-start justify-between mb-6">
            <div>
              <h3
                class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1"
              >
                Roza {{ selectedDay.rozaNumber }}
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ formatGregorianDate(selectedDay.gregorianDate) }}
              </p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {{ selectedDay.hijriDate }}
              </p>
            </div>

            <UButton
              icon="i-heroicons-x-mark"
              size="xs"
              color="gray"
              variant="ghost"
              @click="showDayDetail = false"
            />
          </div>

          <!-- Times -->
          <div class="space-y-3">
            <div
              class="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
            >
              <span class="text-sm text-gray-600 dark:text-gray-400">
                Sehri ends
              </span>
              <span
                class="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100"
              >
                {{ formatTime(selectedDay.sehriEnds) }}
              </span>
            </div>

            <div
              class="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
            >
              <span class="text-sm text-gray-600 dark:text-gray-400">
                Iftar time
              </span>
              <span
                class="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100"
              >
                {{ formatTime(selectedDay.iftarTime) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <div class="px-4 sm:px-6 pb-4">
      <InternalLinks current="calendar" />
    </div>
  </section>
</template>

<script setup lang="ts">
import InternalLinks from "@/components/seo/InternalLinks.vue";
import {
  buildCalendarShareText,
  buildWhatsAppUrl,
} from "@/composables/useWhatsAppShare";
import { useRamadanTime, type RamadanDay } from "@/composables/useRamadanTime";
import { seoConfig } from "@/config/seo.config";
import { computed, ref } from "vue";

useHead({
  title: seoConfig.calendar.title,
  meta: [
    {
      name: "description",
      content: seoConfig.calendar.description,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: seoConfig.calendar.canonical,
    },
  ],
});
/* ----------------------------------
   Core Data (Read-only)
---------------------------------- */
const { normalizedDays, now, cityData } = useRamadanTime();
const calendarHeaderTitle = computed(
  () => `Ramzan 2026 Calendar - ${cityData.value.cityName}`,
);
const calendarShareUrl = computed(() => {
  const message = buildCalendarShareText({
    city: cityData.value.cityName,
    url: seoConfig.calendar.canonical,
  });
  return buildWhatsAppUrl(message);
});

/* ----------------------------------
   Modal State
---------------------------------- */
const showDayDetail = ref(false);
const selectedDay = ref<RamadanDay | null>(null);
const lastRamadanDate = computed(() => {
  for (let i = normalizedDays.value.length - 1; i >= 0; i -= 1) {
    const day = normalizedDays.value[i];
    if (!day?.isRamadan) continue;
    const parsed = parseISODate(day.gregorianDate);
    if (parsed) return parsed;
  }

  return null;
});

/* ----------------------------------
   Helpers (Pure Functions)
---------------------------------- */
const isToday = (gregorianDate: string): boolean => {
  const todayString = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Karachi",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now.value);
  return gregorianDate === todayString;
};

const shouldShowAshraDivider = (day: RamadanDay, index: number): boolean => {
  if (day.isEid) return false;
  if (index === 0) return true;

  const prevDay = normalizedDays.value[index - 1];
  return prevDay && prevDay.ashra !== day.ashra;
};

const getAshraLabel = (ashra: number): string => {
  switch (ashra) {
    case 1:
      return "— Pehla Ashra —";
    case 2:
      return "— Dusra Ashra —";
    case 3:
      return "— Teesra Ashra —";
    default:
      return "";
  }
};

const parseISODate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  const parsed = new Date(dateStr + "T00:00:00");
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatGregorianDate = (dateStr: string): string => {
  const date = parseISODate(dateStr);
  if (!date) return "";

  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
  });
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getEidDateDisplay = (day: RamadanDay): string => {
  const directDate = formatGregorianDate(day.gregorianDate);
  if (directDate) return directDate;

  if (!lastRamadanDate.value) return "Moon sighting ke mutabiq";

  const eidIf29 = addDays(lastRamadanDate.value, 1);
  const eidIf30 = addDays(lastRamadanDate.value, 2);

  const d1 = eidIf29.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  const d2 = eidIf30.toLocaleDateString("en-US", { day: "numeric", month: "short" });

  return `${d1} ya ${d2}`;
};

const formatTime = (time24: string): string => {
  const [hours, minutes] = time24.split(":");
  const h = parseInt(hours);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

/* ----------------------------------
   Actions
---------------------------------- */
const handleDayClick = (day: RamadanDay) => {
  selectedDay.value = day;
  showDayDetail.value = true;
};
</script>
